/**
 * Custom Cart Date Field
 * Handles the functionality for date inputs in the cart using Flatpickr
 */
class CustomCartDateField {
  constructor() {
    this.init();
  }

  init() {
    this.waitForFlatpickr().then(() => {
      this.initializeCalendarFields();
    });
  }

  /**
   * Wait for Flatpickr to be available (since scripts are deferred)
   */
  waitForFlatpickr() {
    return new Promise((resolve) => {
      if (typeof flatpickr !== 'undefined') {
        resolve();
        return;
      }
      const checkInterval = setInterval(() => {
        if (typeof flatpickr !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
    });
  }

  /**
   * Format date as YYYY-MM-DD string
   */
  formatDate(date) {
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  }

  /**
   * Adjust date to skip weekends, Monday, and holidays - move forward until a valid delivery day
   */
  skipNonDeliveryDays(date, holidays) {
    // Keep advancing while the day is Sat/Sun/Mon or a holiday
    while (
      date.getDay() === 0 ||
      date.getDay() === 1 ||
      date.getDay() === 6 ||
      (holidays && holidays.includes(this.formatDate(date)))
    ) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  /**
   * Initialize date fields with Flatpickr calendar functionality
   */
  initializeCalendarFields() {
    const dateWrappers = document.querySelectorAll('.cart-field-date-wrapper');
    let dateOffset = 0;
    if (window.dateOffsetMetafield && !isNaN(parseInt(window.dateOffsetMetafield))) {
      dateOffset = parseInt(window.dateOffsetMetafield);
    }

    const metafieldDefined = !(
      window.dateOffsetMetafield === undefined ||
      window.dateOffsetMetafield === null ||
      window.dateOffsetMetafield === ''
    );

    dateWrappers.forEach((wrapper) => {
      const dateInput = wrapper.querySelector('input[type="date"]');
      const calendarButton = wrapper.querySelector('.cart-field-calendar-button');

      if (!dateInput) return;

      // Destroy existing Flatpickr instance if present (for reinit scenarios)
      if (dateInput._flatpickr) {
        dateInput._flatpickr.destroy();
      }

      const now = new Date();
      const utahTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }));

      let baseOffset = isNaN(dateOffset) ? 0 : dateOffset;
      if (!metafieldDefined) {
        baseOffset = 1;
      }

      let extraOffset = 0;
      const attrExtra = dateInput.getAttribute('data-extra-offset');
      if (attrExtra && !isNaN(parseInt(attrExtra))) {
        extraOffset = parseInt(attrExtra);
      }
      if (extraOffset < 0) extraOffset = 0;

      const totalOffset = baseOffset + extraOffset;
      utahTime.setDate(utahTime.getDate() + totalOffset);

      // Company holidays — update these dates once a year
      const holidays = [
        '2026-01-01', // New Year's Day
        '2026-02-16', // President's Day
        '2026-05-25', // Memorial Day
        '2026-07-04', // Independence Day
        '2026-07-24', // Pioneer Day
        '2026-09-07', // Labor Day
        '2026-11-26', // Thanksgiving Day
        '2026-11-27', // Black Friday
        '2026-12-24', // Christmas Eve
        '2026-12-25', // Christmas Day
        '2026-12-31', // New Year's Eve
      ];

      // Skip weekends, Mondays, and holidays for minimum date
      const minDate = this.skipNonDeliveryDays(utahTime, holidays);

      // Calculate max date: 30 days from today, or 60 days if offset > 25
      const maxDays = totalOffset > 25 ? 60 : 30;
      const maxDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }));
      maxDate.setDate(maxDate.getDate() + maxDays);

      // Initialize Flatpickr
      const fp = flatpickr(dateInput, {
        minDate: minDate,
        maxDate: maxDate,
        dateFormat: 'Y-m-d',
        disableMobile: true,
        // Disable weekends (Saturday = 6, Sunday = 0), Monday (1), and holidays
        disable: [
          (date) => {
            if (date.getDay() === 0 || date.getDay() === 1 || date.getDay() === 6) return true;
            return holidays.includes(this.formatDate(date));
          },
        ],
        onChange: function (selectedDates, dateStr) {
          if (dateStr) {
            dateInput.blur();
          }
        },
      });

      // Store reference for data attributes
      dateInput.setAttribute('data-calculated-min', minDate.toISOString().split('T')[0]);
      dateInput.setAttribute('data-calculated-max', maxDate.toISOString().split('T')[0]);
      dateInput.setAttribute('data-total-offset', totalOffset);

      // Handle calendar button click
      if (calendarButton) {
        calendarButton.addEventListener('click', () => {
          fp.open();
        });
      }
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CustomCartDateField();
});

// Check if we need to reinitialize on cart refreshes or AJAX updates
document.addEventListener('cart:refresh', () => {
  setTimeout(() => {
    new CustomCartDateField();
  }, 100);
});
